import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { EVENT_STATUSES, PAYMENT_STATUSES, CITIES, CAD, statusTone } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DeleteConfirmDialog } from "@/components/admin/DeleteConfirmDialog";
import { Search, MapPin, Users, CalendarDays, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { toast } from "sonner";

const VIEWS = [["active", "Active"], ["archived", "Archived"], ["all", "All"]];
const todayStr = () => new Date().toISOString().slice(0, 10);

export const isProtectedEvent = (e) => {
  if (!e) return false;
  const signed = e.contract_status === "Signed" || !!(e.contract && e.contract.signature);
  const activeStatus = ["Confirmed", "Deposit paid", "Preparing"].includes(e.event_status);
  const upcoming = e.event_date && e.event_date >= todayStr() && !["Cancelled", "Completed"].includes(e.event_status);
  return signed || activeStatus || upcoming;
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("active");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [payFilter, setPayFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [testOnly, setTestOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get(`/events?view=${view}`).then((r) => { setEvents(r.data); setSelectedIds([]); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view]);

  const isTest = (e) => {
    const hay = `${e.full_name || ""} ${e.email || ""}`.toLowerCase();
    return hay.includes("example.com") || hay.includes("test") || hay.includes("demo");
  };

  const filtered = useMemo(() => events.filter((e) => {
    const s = search.toLowerCase();
    const match = !s || [e.full_name, e.email, e.phone, e.event_city, e.event_type].some((v) => (v || "").toLowerCase().includes(s));
    const t = !testOnly || isTest(e);
    const df = !dateFrom || (e.event_date && e.event_date >= dateFrom);
    const dt = !dateTo || (e.event_date && e.event_date <= dateTo);
    return match && (!statusFilter || e.event_status === statusFilter) && (!payFilter || e.payment_status === payFilter) && (!cityFilter || e.event_city === cityFilter) && t && df && dt;
  }), [events, search, statusFilter, payFilter, cityFilter, testOnly, dateFrom, dateTo]);

  const toggleSelect = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allVisibleSelected = filtered.length > 0 && filtered.every((e) => selectedIds.includes(e.id));
  const toggleSelectAll = () => setSelectedIds(allVisibleSelected ? [] : filtered.map((e) => e.id));

  const archiveEvent = async (id, archived) => {
    await api.patch(`/events/${id}/archive`, { archived });
    toast.success(archived ? "Event archived" : "Event restored"); load();
  };
  const deleteEvent = async (id) => {
    await api.delete(`/events/${id}`);
    toast.success("Event permanently deleted"); load();
  };
  const bulkAction = async (action) => {
    if (!selectedIds.length) return;
    const n = selectedIds.length;
    await api.post("/events/bulk", { ids: selectedIds, action });
    toast.success(`${action === "delete" ? "Deleted" : action === "archive" ? "Archived" : "Restored"} ${n} event(s)`);
    load();
  };

  const protectedSelectedCount = useMemo(() => events.filter((e) => selectedIds.includes(e.id) && isProtectedEvent(e)).length, [events, selectedIds]);

  return (
    <div className="animate-fade-in">
      <div className="mb-6"><p className="eyebrow mb-2">Bookings</p><h1 className="font-serif text-4xl">Events</h1></div>

      <div className="flex items-center gap-2 mb-4" data-testid="events-view-tabs">
        {VIEWS.map(([v, label]) => (
          <button key={v} onClick={() => setView(v)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === v ? "bg-ink text-white" : "bg-clay text-muted-foreground hover:text-ink"}`} data-testid={`events-view-${v}`}>{label}</button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search events…" className="df-input pl-10" data-testid="events-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="df-input max-w-[180px]" data-testid="events-status-filter">
          <option value="">All statuses</option>{EVENT_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={payFilter} onChange={(e) => setPayFilter(e.target.value)} className="df-input max-w-[180px]" data-testid="events-payment-filter">
          <option value="">All payments</option>{PAYMENT_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} className="df-input max-w-[160px]" data-testid="events-city-filter">
          <option value="">All cities</option>{CITIES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-5">
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="events-test-only"><Checkbox checked={testOnly} onCheckedChange={(v) => setTestOnly(!!v)} /> Test/demo only</label>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Event date</span>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="df-input max-w-[160px]" data-testid="events-date-from" />
          <span className="text-muted-foreground">to</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="df-input max-w-[160px]" data-testid="events-date-to" />
        </div>
        {filtered.length > 0 && (
          <label className="flex items-center gap-2 text-sm cursor-pointer ml-auto" data-testid="events-select-all"><Checkbox checked={allVisibleSelected} onCheckedChange={toggleSelectAll} /> Select all ({filtered.length})</label>
        )}
      </div>

      {selectedIds.length > 0 && (
        <div className="df-card p-3 mb-4 flex flex-wrap items-center gap-2 sticky top-2 z-20 bg-cream/95 backdrop-blur" data-testid="events-bulk-bar">
          <span className="text-sm font-medium mr-1">{selectedIds.length} selected</span>
          {view !== "archived" && <Button onClick={() => bulkAction("archive")} className="btn-outline text-xs" data-testid="events-bulk-archive"><Archive className="h-3 w-3" /> Archive</Button>}
          {view !== "active" && <Button onClick={() => bulkAction("unarchive")} className="btn-outline text-xs" data-testid="events-bulk-restore"><ArchiveRestore className="h-3 w-3" /> Restore</Button>}
          <Button onClick={() => setBulkDeleteOpen(true)} className="text-xs bg-destructive text-white hover:bg-destructive/90 h-9 px-3" data-testid="events-bulk-delete"><Trash2 className="h-3 w-3" /> Delete</Button>
          <button onClick={() => setSelectedIds([])} className="text-xs text-muted-foreground ml-auto">Clear</button>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filtered.length === 0 && <p className="text-sm text-muted-foreground py-16 text-center col-span-full">No events found.</p>}
        {filtered.map((e) => (
          <div key={e.id} className={`df-card p-6 relative ${selectedIds.includes(e.id) ? "ring-2 ring-olive" : ""} ${e.archived ? "opacity-70" : ""}`} data-testid={`event-card-${e.id}`}>
            <div className="absolute top-5 left-5 z-10" onClick={(ev) => ev.stopPropagation()}>
              <Checkbox checked={selectedIds.includes(e.id)} onCheckedChange={() => toggleSelect(e.id)} data-testid={`event-select-${e.id}`} />
            </div>
            {e.archived && <span className="absolute top-4 right-4 text-[9px] uppercase tracking-widest bg-clay text-muted-foreground px-2 py-0.5 rounded-full">Archived</span>}
            <button onClick={() => navigate(`/admin/events/${e.id}`)} className="text-left w-full pl-8">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div><h3 className="font-serif text-2xl leading-tight">{e.full_name}</h3><p className="text-sm text-muted-foreground">{e.event_type}</p></div>
                <Badge variant="outline" className={statusTone(e.event_status)}>{e.event_status}</Badge>
              </div>
              <div className="space-y-2 text-sm text-muted-foreground mb-4">
                <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4" /> {e.event_date || "Date TBD"}</div>
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {e.event_city || "—"}</div>
                <div className="flex items-center gap-2"><Users className="h-4 w-4" /> {e.guest_count || 0} guests</div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-line">
                <span className="font-serif text-xl">{CAD(e.quote?.total)}</span>
                <Badge variant="outline" className={statusTone(e.payment_status)}>{e.payment_status}</Badge>
              </div>
            </button>
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-line">
              {e.archived
                ? <button onClick={() => archiveEvent(e.id, false)} className="text-xs text-olive flex items-center gap-1" data-testid={`event-restore-${e.id}`}><ArchiveRestore className="h-3 w-3" /> Restore</button>
                : <button onClick={() => archiveEvent(e.id, true)} className="text-xs text-muted-foreground hover:text-ink flex items-center gap-1" data-testid={`event-archive-${e.id}`}><Archive className="h-3 w-3" /> Archive</button>}
              <button onClick={() => setDeleteTarget(e)} className="text-xs text-destructive flex items-center gap-1 ml-auto" data-testid={`event-delete-${e.id}`}><Trash2 className="h-3 w-3" /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteEvent(deleteTarget.id)}
        description="Are you sure you want to permanently delete this event and all related records (quote, contract, invoice, menu, alcohol list, supplies, checklist, CRM timeline, related webhook logs, and the originating lead)? This action cannot be undone."
        protectedWarning={deleteTarget && isProtectedEvent(deleteTarget) ? "This event appears active or confirmed. Please archive it first or change the status before deleting." : ""}
      />

      <DeleteConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        count={selectedIds.length}
        onConfirm={() => bulkAction("delete")}
        description={`Are you sure you want to permanently delete ${selectedIds.length} event(s) and all their related records? This action cannot be undone.`}
        protectedWarning={protectedSelectedCount > 0 ? `${protectedSelectedCount} of the selected events appear active or confirmed. Consider archiving them instead.` : ""}
      />
    </div>
  );
}
